import { useState, memo } from 'react'
import { Download, Eye, FileText, Plus, Tag, Trash2, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  UnifiedDocument,
  DocumentModuleConfig,
  DocumentUploadData,
  DocumentFilters,
  DocumentActionEvent
} from './types/document-types'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { DocumentPreview } from './DocumentPreview'
import { DocumentDescription, MAX_DESCRIPTION_LENGTH } from './DocumentDescription'
import { useDateTimeFormat } from '@/hooks/useDateTimeFormat'

import { getErrorMessage } from '../../utils/error.utils'
import { NativeSelect } from "@/components/ui/native-select"

// Upload Form Component - Extracted to prevent re-creation on every render
interface UploadFormProps {
  config: DocumentModuleConfig
  uploadData: Partial<DocumentUploadData>
  setUploadData: React.Dispatch<React.SetStateAction<Partial<DocumentUploadData>>>
  selectedFiles: File[]
  setSelectedFiles: React.Dispatch<React.SetStateAction<File[]>>
  uploading: boolean
  error: string | null
  onSubmit: (e: React.FormEvent) => Promise<void>
  onCancel: () => void
}

const UploadForm = memo(({
  config,
  uploadData,
  setUploadData,
  selectedFiles,
  setSelectedFiles,
  uploading,
  error,
  onSubmit,
  onCancel
}: UploadFormProps) => {
  const [dragActive, setDragActive] = useState(false)

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const files = Array.from(e.dataTransfer.files).slice(0, config.ui.maxFilesAtOnce)
      setSelectedFiles(files)
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const form = (
        <form onSubmit={onSubmit} className={config.ui.modalMode ? 'space-y-4' : 'space-y-4 p-4 sm:p-6'}>
          {/* File Upload */}
          <div>
            <Label className="mb-2">
              Arquivo{selectedFiles.length > 1 ? 's' : ''} *
            </Label>
            {config.features.dragDrop ? (
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center ${
                  dragActive ? 'border-primary bg-primary/5' : 'border-input'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  multiple={config.ui.maxFilesAtOnce > 1}
                  accept={config.ui.allowedFileTypes.join(',')}
                  onChange={(e) => setSelectedFiles(Array.from(e.target.files || []))}
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <div className="text-muted-foreground">
                    <p>Arraste arquivos aqui ou clique para selecionar</p>
                    <p className="text-xs mt-1">
                      Máximo {config.ui.maxFilesAtOnce} arquivo{config.ui.maxFilesAtOnce > 1 ? 's' : ''} •
                      Tamanho máximo: {formatFileSize(config.ui.maxFileSize)}
                    </p>
                  </div>
                </label>
              </div>
            ) : (
              <input
                type="file"
                multiple={config.ui.maxFilesAtOnce > 1}
                accept={config.ui.allowedFileTypes.join(',')}
                onChange={(e) => setSelectedFiles(Array.from(e.target.files || []))}
                className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-secondary file:text-secondary-foreground hover:file:bg-accent"
              />
            )}

            {selectedFiles.length > 0 && (
              <div className="mt-2 space-y-1">
                <p className="text-xs text-muted-foreground">O arquivo fica com o nome original.</p>
                {selectedFiles.map((file, index) => (
                  <div key={index} className="text-sm text-muted-foreground flex items-center justify-between">
                    <span>{file.name} ({formatFileSize(file.size)})</span>
                    <button
                      type="button"
                      onClick={() => setSelectedFiles(files => files.filter((_, i) => i !== index))}
                      className="rounded-sm text-destructive hover:text-destructive/80"
                      aria-label={`Remover ${file.name}`}
                    >
                      <X className="size-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Document Type */}
          <div>
            <Label htmlFor="document-upload-type" className="mb-1">
              Tipo de documento
            </Label>
            <NativeSelect
              id="document-upload-type"
              value={uploadData.documentType || ''}
              onChange={(e) => setUploadData({...uploadData, documentType: e.target.value})}
              className="w-full"
            >
              <option value="">Outro / não classificar</option>
              {config.documentTypes.map(type => (
                <option key={type.key} value={type.key}>
                  {type.label}
                  {config.ui.showDescription && type.description && ` - ${type.description}`}
                </option>
              ))}
            </NativeSelect>
          </div>

          {/* Finalidade (opcional) */}
          <div>
            <Label htmlFor="document-upload-description" className="mb-1">
              Para que serve este arquivo? <span className="font-normal text-muted-foreground">(opcional)</span>
            </Label>
            <Input
              id="document-upload-description"
              type="text"
              maxLength={MAX_DESCRIPTION_LENGTH}
              value={uploadData.description || ''}
              onChange={(e) => setUploadData({...uploadData, description: e.target.value})}
              placeholder="Ex.: procuração assinada para o INSS"
            />
          </div>

          {/* Confidential */}
          {config.features.confidential && (
            <div className="flex items-center">
              <input
                type="checkbox"
                id="confidential"
                checked={uploadData.isConfidential || false}
                onChange={(e) => setUploadData({...uploadData, isConfidential: e.target.checked})}
                className="h-4 w-4 rounded-sm border-input accent-primary"
              />
              <label htmlFor="confidential" className="ml-2 block text-sm text-foreground">
                Documento confidencial
              </label>
            </div>
          )}

          {/* Tags */}
          {config.ui.showTags && (
            <div>
              <Label htmlFor="document-upload-tags" className="mb-1">
                Tags
              </Label>
              <Input
                id="document-upload-tags"
                type="text"
                value={uploadData.tags?.join(', ') || ''}
                onChange={(e) => setUploadData({...uploadData, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean)})}
                placeholder="Separadas por vírgula"
              />
            </div>
          )}

          {error && (
            <div role="alert" className="text-sm text-destructive">{error}</div>
          )}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancelar
            </Button>
            <Button
              type="submit"
              loading={uploading}
              disabled={uploading || !selectedFiles.length}
            >
              {uploading ? 'Enviando...' : 'Fazer Upload'}
            </Button>
          </div>
        </form>
  )

  if (config.ui.modalMode) {
    return (
      <Dialog open onOpenChange={(open) => { if (!open) onCancel() }}>
        <DialogContent size="lg" fullScreenOnMobile className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Adicionar Documento</DialogTitle>
          </DialogHeader>
          {form}
        </DialogContent>
      </Dialog>
    )
  }

  return <div className="bg-muted/50 border-b">{form}</div>
})

interface UnifiedDocumentManagerProps {
  config: DocumentModuleConfig
  entityId: string
  entityName?: string
  documents: UnifiedDocument[]
  loading?: boolean
  onUpload: (data: DocumentUploadData) => Promise<void>
  onDelete: (documentId: string) => Promise<void>
  onDownload: (document: UnifiedDocument) => Promise<void>
  onPreview: (document: UnifiedDocument) => Promise<Blob>
  /** Grava a finalidade do documento (null apaga). Sem ela, a descrição só é exibida. */
  onUpdateDescription?: (document: UnifiedDocument, description: string | null) => Promise<void>
  onAction?: (event: DocumentActionEvent) => void
  className?: string
}

export function UnifiedDocumentManager({
  config,
  entityId: _entityId,
  entityName,
  documents,
  loading = false,
  onUpload,
  onDelete,
  onDownload,
  onPreview,
  onUpdateDescription,
  onAction: _onAction,
  className = ''
}: UnifiedDocumentManagerProps) {
  const { formatDateTime } = useDateTimeFormat()
  const [showUploadForm, setShowUploadForm] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<DocumentFilters>({})
  const [previewDocument, setPreviewDocument] = useState<UnifiedDocument | null>(null)

  // Upload form state
  const [uploadData, setUploadData] = useState<Partial<DocumentUploadData>>({
    documentType: '',
    title: '',
    description: '',
    isConfidential: false,
    tags: []
  })
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])

  const filteredDocuments = documents.filter(doc => {
    if (filters.search && !doc.title.toLowerCase().includes(filters.search.toLowerCase()) && 
        !doc.fileName?.toLowerCase().includes(filters.search.toLowerCase()) &&
        !doc.description?.toLowerCase().includes(filters.search.toLowerCase())) {
      return false
    }
    if (filters.documentType && doc.documentType !== filters.documentType) {
      return false
    }
    if (filters.isConfidential !== undefined && doc.isConfidential !== filters.isConfidential) {
      return false
    }
    return true
  })

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedFiles.length) return

    try {
      setUploading(true)
      setError(null)

      for (const file of selectedFiles) {
        const data: DocumentUploadData = {
          file,
          title: uploadData.title || file.name,
          documentType: uploadData.documentType || 'OTHER',
          description: uploadData.description?.trim() || undefined,
          isConfidential: uploadData.isConfidential || false,
          tags: uploadData.tags
        }
        await onUpload(data)
      }

      // Reset form
      setSelectedFiles([])
      setUploadData({
        documentType: '',
        title: '',
        description: '',
        isConfidential: false,
        tags: []
      })
      setShowUploadForm(false)
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao fazer upload do documento'))
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (documentId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este documento?')) return

    try {
      setError(null)
      await onDelete(documentId)
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao excluir documento'))
    }
  }

  const handlePreview = async (document: UnifiedDocument) => {
    try {
      setError(null)
      const blob = await onPreview(document)
      if (blob.size === 0) {
        throw new Error('Arquivo de preview vazio')
      }

      const previewUrl = window.URL.createObjectURL(blob)
      setPreviewDocument({ ...document, filePath: previewUrl })
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao carregar preview do documento'))
    }
  }

  const closePreview = () => {
    if (previewDocument?.filePath?.startsWith('blob:')) {
      window.URL.revokeObjectURL(previewDocument.filePath)
    }
    setPreviewDocument(null)
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const getDocumentTypeConfig = (typeKey: string) => {
    return config.documentTypes.find(dt => dt.key === typeKey)
  }

  const DocumentIcon = ({ document }: { document: UnifiedDocument }) => {
    const typeConfig = getDocumentTypeConfig(document.documentType)
    if (!typeConfig) return null
    
    const Icon = typeConfig.icon
    return <Icon className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className={`bg-card shadow-sm rounded-lg ${className}`}>
      {/* Header */}
      <div className="px-4 sm:px-6 py-4 border-b">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-lg font-medium text-foreground">
            {config.title}{entityName ? ` - ${entityName}` : ''}
          </h3>
          <Button variant="outline" onClick={() => setShowUploadForm(true)} className="self-start sm:self-auto">
            <Plus className="size-4" />
            Adicionar Documento
          </Button>
        </div>

        {/* Filters */}
        {config.features.search && (
          <div className="mt-4 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Input
              type="text"
              placeholder="Buscar documentos..."
              aria-label="Buscar documentos"
              value={filters.search || ''}
              onChange={(e) => setFilters({...filters, search: e.target.value})}
              className="flex-1 sm:min-w-64"
            />
            
            {config.features.categories && (
              <NativeSelect
                value={filters.documentType || ''}
                onChange={(e) => setFilters({...filters, documentType: e.target.value || undefined})}
              >
                <option value="">Todos os tipos</option>
                {config.documentTypes.map(type => (
                  <option key={type.key} value={type.key}>{type.label}</option>
                ))}
              </NativeSelect>
            )}

            {config.features.confidential && (
              <NativeSelect
                value={filters.isConfidential === undefined ? '' : filters.isConfidential.toString()}
                onChange={(e) => setFilters({...filters, isConfidential: e.target.value === '' ? undefined : e.target.value === 'true'})}
              >
                <option value="">Todos</option>
                <option value="false">Públicos</option>
                <option value="true">Confidenciais</option>
              </NativeSelect>
            )}
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div role="alert" className="mx-4 mt-4 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive sm:mx-6">
          {error}
        </div>
      )}

      {/* Upload Form */}
      {showUploadForm && (
        <UploadForm
          config={config}
          uploadData={uploadData}
          setUploadData={setUploadData}
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
          uploading={uploading}
          error={error}
          onSubmit={handleUpload}
          onCancel={() => setShowUploadForm(false)}
        />
      )}

      {/* Documents List */}
      <div className="px-4 sm:px-6 py-4">
        {filteredDocuments.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="mx-auto mb-4 size-12 text-muted-foreground" aria-hidden="true" />
            <h3 className="text-sm font-medium text-foreground">Nenhum documento</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Comece adicionando um documento.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredDocuments.map((document) => {
              const typeConfig = getDocumentTypeConfig(document.documentType)
              return (
                <div
                  key={document.id}
                  className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-4 border rounded-lg hover:bg-muted/50"
                >
                  <div className="flex items-start sm:items-center gap-3 min-w-0 flex-1">
                    <DocumentIcon document={document} />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="min-w-0 break-all text-sm font-medium text-foreground">
                          {document.fileName || document.title}
                        </p>
                        {document.isConfidential && (
                          <Badge variant="destructive">Confidencial</Badge>
                        )}
                        {document.version && document.version > 1 && (
                          <Badge variant="secondary">v{document.version}</Badge>
                        )}
                      </div>
                      <DocumentDescription
                        name={document.fileName || document.title}
                        description={document.description}
                        onSave={onUpdateDescription ? (description) => onUpdateDescription(document, description) : undefined}
                      />
                      {document.tags && document.tags.length > 0 && (
                        <div className="flex items-center gap-1 mt-1">
                          <Tag className="size-3 text-muted-foreground" aria-hidden="true" />
                          {document.tags.map(tag => (
                            <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium bg-muted text-foreground">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {typeConfig?.label || document.documentType}
                        {document.uploadedByName && ` • Por ${document.uploadedByName}`}
                        {document.createdAt && ` • ${formatDateTime(document.createdAt)}`}
                        {document.fileSize && ` • ${formatFileSize(document.fileSize)}`}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-end gap-2 self-end sm:self-auto shrink-0">
                    {config.features.preview && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="icon-sm" onClick={() => void handlePreview(document)} aria-label="Visualizar">
                            <Eye className="size-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Visualizar</TooltipContent>
                      </Tooltip>
                    )}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon-sm" onClick={() => onDownload(document)} aria-label="Baixar">
                          <Download className="size-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Baixar</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => handleDelete(document.id)}
                          className="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                          aria-label="Excluir"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Excluir</TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Document Preview Modal */}
      {previewDocument && (
        <DocumentPreview
          document={previewDocument}
          onClose={closePreview}
          onDownload={() => onDownload(previewDocument)}
        />
      )}
    </div>
  )
}